import {DefaultPluginConfig, registerPlugin} from "../lib/plugin";

type Items = {
    title: string
    token: string
}[]

export interface TipMenuConfig extends DefaultPluginConfig {
    items: string
    size: number
    family: string
    weight: string
    color: string
    tableBorderColor: string
    tableBorderWidth: number
    tableBorderInnerColor: string
    tableBorderInnerWidth: number
    tableBackgroundTransparent: number
    tableBackground: string
    outerPaddingHorizontal: number
    outerPaddingVertical: number
    innerPaddingHorizontal: number
    innerPaddingVertical: number
    horizontal: 'left'|'right'
    horizontalSpace: number
    vertical: 'top'|'bottom'
    verticalSpace: number
}

const render = (id: string, config: any, parentNode = null) => {
    if (!parentNode) {
        parentNode = document.body
    }
    if (typeof document === 'undefined') {
        return
    }
    let field = document.getElementById('plugin--tipmenu--' + id)
    if (field) {
        parentNode = field.parentNode
        field.parentNode.removeChild(field)
    }

    let styleTag = document.getElementById('plugin--tipmenu--' + id + '--style')
    if (styleTag) {
        styleTag.parentNode.removeChild(styleTag)
    }

    let stylesheet = ''

    const items: Items = config.items.split("\n").map(line => {
        line = line.trim()
        if (!line || !line.includes('|')) {
            return null
        }
        const [ key, value ] = line.split('|')
        return {
            title: key.trim(),
            token: value.trim(),
        }
    }).filter(item => !!item)

    if (!config.enabled) {
        return
    }

    if (!items.length) {
        return
    }

    field = document.createElement('div')
    field.id = 'plugin--tipmenu--' + id

    if (parentNode === document.body) {
        field.style.position = 'absolute'
    }

    if (config.horizontal === 'left') {
        field.style.left = `${config.horizontalSpace}px`
    } else {
        field.style.right = `${config.horizontalSpace}px`
    }

    if (config.vertical === 'top') {
        field.style.top = `${config.verticalSpace}px`
    } else {
        field.style.bottom = `${config.verticalSpace}px`
    }

    const tableWrap = document.createElement('div')
    tableWrap.classList.add('table-wrap')

    tableWrap.style.borderStyle = 'solid'
    tableWrap.style.position = 'relative'
    tableWrap.style.borderColor = config.tableBorderColor
    tableWrap.style.borderWidth = config.tableBorderWidth
    tableWrap.style.borderRadius = `${config.tableBorderRadius}px`
    tableWrap.style.padding = `${config.outerPaddingVertical}px ${config.outerPaddingHorizontal}px`

    const table = document.createElement('table')
    table.style.borderCollapse = 'collapse'
    table.style.fontSize = `${config.size}px`
    table.style.fontFamily = config.family
    table.style.fontWeight = config.weight
    table.style.color = config.color

    for (const item of items) {
        const tr = document.createElement('tr')

        const tdTitle = document.createElement('td')
        tdTitle.innerText = item.title
        tdTitle.style.borderStyle = 'solid'
        tdTitle.style.borderColor = config.tableBorderInnerColor
        tdTitle.style.borderWidth = config.tableBorderInnerWidth
        tdTitle.style.padding = `${config.innerPaddingVertical}px ${config.innerPaddingHorizontal / 2}px ${config.innerPaddingVertical}px ${config.innerPaddingHorizontal}px`

        const tdToken = document.createElement('td')
        tdToken.innerText = `${item.token} Token`
        tdToken.style.borderStyle = 'solid'
        tdToken.style.borderColor = config.tableBorderInnerColor
        tdToken.style.borderWidth = config.tableBorderInnerWidth
        tdToken.style.padding = `${config.innerPaddingVertical}px ${config.innerPaddingHorizontal}px ${config.innerPaddingVertical}px ${config.innerPaddingHorizontal / 2}px`

        tr.appendChild(tdTitle)
        tr.appendChild(tdToken)

        table.appendChild(tr)
    }

    tableWrap.appendChild(table)

    field.appendChild(tableWrap)

    parentNode.appendChild(field)

    if (config.tableBackgroundTransparent < 100) {
        stylesheet += `
#plugin--tipmenu--${id} .table-wrap::before {
    content: '';
    display: block;
    background: ${config.tableBackground};
    opacity: ${1 - (config.tableBackgroundTransparent / 100)};
    border-radius: ${config.tableBorderRadius}px;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
}
        `
    }

    if (stylesheet) {
        styleTag = document.createElement('style')
        styleTag.id = 'plugin--tipmenu--' + id + '--style'
        styleTag.innerText = stylesheet.replaceAll("\n", ' ')
        console.log('appending stylesheet', styleTag, parentNode)
        parentNode.appendChild(styleTag)
    } else {
        console.log('no stylesheet')
    }
}

registerPlugin<TipMenuConfig>('tipmenu', 'Tip-Menu', {
    defaultSettings: [ "enabled", "items", "horizontal", "vertical" ],

    supports: ['position'],

    settings: {
        items: {
            title: 'Items',
            type: 'textarea',
            help: 'Each item per line - description and token amount separated by "|"',
            rows: 6,
            defaultValue: `
Item 1: | 10
Item 2: | 20
Item 3: | 30
            `.trim(),
        },
        groupTable: {
            type: 'group',
            title: 'Table',
            items: {
                size: {
                    title: 'Size',
                    type: 'number',
                    defaultValue: 22,
                },
                family: {
                    title: 'Font-Family',
                    type: 'select',
                    options: ['serif', 'sans-serif'],
                    defaultValue: 'sans-serif',
                },
                weight: {
                    title: 'Font weight',
                    type: 'select',
                    options: ['normal', 'bold'],
                    defaultValue: 'normal',
                },
                color: {
                    title: 'Color',
                    type: 'color',
                    defaultValue: '#000000',
                },
                tableBorderColor: {
                    title: 'Border color',
                    type: 'color',
                    defaultValue: '#000000',
                },
                tableBorderWidth: {
                    title: 'Border outer width',
                    type: 'number',
                    defaultValue: 0,
                },
                tableBorderInnerColor: {
                    title: 'Border inner color',
                    type: 'color',
                    defaultValue: '#efefef',
                },
                tableBorderInnerWidth: {
                    title: 'Border inner width',
                    type: 'number',
                    defaultValue: 0,
                },
                tableBorderRadius: {
                    title: 'Rounded corners',
                    type: 'number',
                    defaultValue: 10,
                },
                tableBackgroundTransparent: {
                    title: 'Background transparency',
                    type: 'number',
                    min: 0,
                    max: 100,
                    defaultValue: 40,
                },
                tableBackground: {
                    title: 'Background color',
                    type: 'color',
                    defaultValue: '#fff',
                },
            }
        },
        groupOuterSpace: {
            type: 'group',
            title: 'Outer Space',
            items: {
                outerPaddingHorizontal: {
                    title: 'Horizontal',
                    type: 'number',
                    defaultValue: 5,
                },
                outerPaddingVertical: {
                    title: 'Vertical',
                    type: 'number',
                    defaultValue: 5,
                },
            }
        },
        groupInnerSpace: {
            type: 'group',
            title: 'Inner Space',
            items: {
                innerPaddingHorizontal: {
                    title: 'Horizontal',
                    type: 'number',
                    defaultValue: 13,
                },
                innerPaddingVertical: {
                    title: 'Vertical',
                    type: 'number',
                    defaultValue: 8,
                },
            }
        },
    },

    initialize: (id, config, parentNode) => {
        console.log('tipmenu plugin init', id, config, parentNode)
        render(id, config, parentNode)
    },

    onConfig: (id, config) => {
        console.log('tipmenu plugin config', id, config)
        render(id, config)
    },

    onRemove: (id) => {
        console.log('removing name plugin', id)
        let field = document.getElementById('plugin--tipmenu--' + id)
        if (field) {
            field.parentNode.removeChild(field)
        }
        let stylesheet = document.getElementById('plugin--tipmenu--' + id + '--style')
        if (field) {
            field.parentNode.removeChild(stylesheet)
        }
    },

    onWsAction: () => false
})